import { useQuery } from '@tanstack/react-query';
import { Braces, ChevronDown, Search, SlidersHorizontal, X } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { integrationsApi, teamsApi, usersApi } from '../../lib/api.js';
import { cx } from '../../lib/cx.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { Select, TextInput } from '../../components/ui/Field.js';
import {
  GROUP_BY_OPTIONS,
  RANGE_OPTIONS,
  SORT_OPTIONS,
  activeFilterCount,
  defaultFilters,
  type AlarmFilters,
  type GroupBy,
  type Sort,
  type TimeRange,
} from './filters.js';

const STATE_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: '', label: 'Any state' },
  { value: 'open', label: 'Open' },
  { value: 'acknowledged', label: 'Acknowledged' },
  { value: 'snoozed', label: 'Snoozed' },
  { value: 'closed', label: 'Closed' },
];

const SEVERITY_OPTIONS = [
  { value: '', label: 'Any severity' },
  { value: 'critical', label: 'Critical' },
  { value: 'major', label: 'Major' },
  { value: 'warning', label: 'Warning' },
  { value: 'info', label: 'Info' },
];

/** Secondary filters live behind the disclosure; these are the ones it holds. */
const SECONDARY_KEYS = ['integration_id', 'team_id', 'assignee_id', 'tag', 'range'] as const;

export interface AlarmFilterBarProps {
  filters: AlarmFilters;
  onChange: (next: AlarmFilters) => void;
  onOpenAdvanced: () => void;
  className?: string;
}

/**
 * The filter bar above the alarm table.
 *
 * One row is always visible — search, state, severity and grouping, the four
 * things an operator changes minute to minute. Integration, team, assignee,
 * tag, time range and sort sit behind a disclosure that opens by itself when
 * any of them is set, so the table starts near the top of the window instead of
 * a third of the way down it.
 */
export function AlarmFilterBar({
  filters,
  onChange,
  onOpenAdvanced,
  className,
}: AlarmFilterBarProps) {
  const secondaryActive = SECONDARY_KEYS.filter(
    (key) => filters[key] && filters[key] !== defaultFilters()[key],
  ).length;
  const [expanded, setExpanded] = useState(secondaryActive > 0);

  const integrations = useQuery({
    queryKey: ['integrations', 'all'],
    queryFn: () => integrationsApi.listAll(),
    staleTime: 5 * 60_000,
    retry: false,
  });
  const teams = useQuery({
    queryKey: ['teams', 'all'],
    queryFn: () => teamsApi.listAll(),
    staleTime: 5 * 60_000,
    retry: false,
  });
  const users = useQuery({
    queryKey: ['users', 'all'],
    queryFn: () => usersApi.listAll(),
    staleTime: 5 * 60_000,
    retry: false,
  });

  const set = <K extends keyof AlarmFilters>(key: K, value: AlarmFilters[K]): void =>
    onChange({ ...filters, [key]: value });

  const count = activeFilterCount(filters);

  return (
    <div
      className={cx(
        'flex flex-col gap-2 rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--surface)] p-2',
        className,
      )}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="relative min-w-[160px] flex-1 sm:max-w-[320px]">
          <Search
            className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--text-3)]"
            aria-hidden
          />
          <TextInput
            aria-label="Search alarms"
            value={filters.q}
            onChange={(event) => set('q', event.target.value)}
            placeholder="Search alarms…"
            className="w-full pl-7"
          />
        </span>

        <span className="w-[136px]">
          <Select
            aria-label="State"
            value={filters.state}
            onChange={(event) => set('state', event.target.value)}
          >
            {STATE_OPTIONS.map((option) => (
              <option key={option.label} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </span>

        <span className="w-[136px]">
          <Select
            aria-label="Severity"
            value={filters.severity}
            onChange={(event) => set('severity', event.target.value)}
          >
            {SEVERITY_OPTIONS.map((option) => (
              <option key={option.label} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </span>

        <span className="hidden w-[182px] sm:block">
          <Select
            aria-label="Group by"
            value={filters.group_by}
            onChange={(event) => set('group_by', event.target.value as GroupBy)}
          >
            {GROUP_BY_OPTIONS.map((option) => (
              <option key={option.label} value={option.value}>
                {groupByLabel(option)}
              </option>
            ))}
          </Select>
        </span>

        <Button
          variant={expanded ? 'subtle' : 'secondary'}
          aria-expanded={expanded}
          // The visible label is hidden on a phone, so the name has to be said.
          aria-label="More filters"
          onClick={() => setExpanded((open) => !open)}
        >
          <SlidersHorizontal className="h-3.5 w-3.5" aria-hidden />
          <span className="hidden sm:inline">More filters</span>
          {secondaryActive > 0 && <Badge className="ml-0.5">{secondaryActive}</Badge>}
          <ChevronDown
            className={cx(
              'h-3.5 w-3.5 transition-transform duration-[var(--dur-fast)]',
              expanded && 'rotate-180',
            )}
            aria-hidden
          />
        </Button>

        {/* `hidden` cannot beat the button's own `inline-flex`; the wrapper can. */}
        <span className="hidden sm:block">
          <Button variant="secondary" onClick={onOpenAdvanced}>
            <Braces className="h-3.5 w-3.5" aria-hidden />
            Advanced
            {filters.dsl && <Badge className="ml-1">DSL</Badge>}
          </Button>
        </span>

        {count > 0 && (
          <Button variant="ghost" onClick={() => onChange(defaultFilters())}>
            <X className="h-3.5 w-3.5" aria-hidden />
            Clear {count}
          </Button>
        )}
      </div>

      {expanded && (
        <div className="grid grid-cols-2 gap-2 border-t border-[var(--border)] pt-2 sm:grid-cols-3 lg:grid-cols-6">
          <FilterField label="Integration">
            <Select
              value={filters.integration_id}
              onChange={(event) => set('integration_id', event.target.value)}
            >
              <option value="">Any integration</option>
              {(integrations.data ?? []).map((integration) => (
                <option key={integration.id} value={integration.id}>
                  {integration.name}
                </option>
              ))}
            </Select>
          </FilterField>

          <FilterField label="Team">
            <Select
              value={filters.team_id}
              onChange={(event) => set('team_id', event.target.value)}
            >
              <option value="">Any team</option>
              {(teams.data ?? []).map((team) => (
                <option key={team.id} value={team.id}>
                  {team.name}
                </option>
              ))}
            </Select>
          </FilterField>

          <FilterField label="Assignee">
            <Select
              value={filters.assignee_id}
              onChange={(event) => set('assignee_id', event.target.value)}
            >
              <option value="">Anyone</option>
              {(users.data ?? []).map((user) => (
                <option key={user.id} value={user.id}>
                  {user.display_name}
                </option>
              ))}
            </Select>
          </FilterField>

          <FilterField label="Tag">
            <TextInput
              value={filters.tag}
              onChange={(event) => set('tag', event.target.value)}
              placeholder="prod"
            />
          </FilterField>

          <FilterField label="Time range">
            <Select
              value={filters.range}
              onChange={(event) => set('range', event.target.value as TimeRange)}
            >
              {RANGE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </FilterField>

          <FilterField label="Sort">
            <Select
              value={filters.sort}
              onChange={(event) => set('sort', event.target.value as Sort)}
            >
              {SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </FilterField>

          <div className="col-span-2 flex gap-2 sm:col-span-3 sm:hidden">
            <span className="min-w-0 flex-1">
              <Select
                aria-label="Group by"
                value={filters.group_by}
                onChange={(event) => set('group_by', event.target.value as GroupBy)}
              >
                {GROUP_BY_OPTIONS.map((option) => (
                  <option key={option.label} value={option.value}>
                    {groupByLabel(option)}
                  </option>
                ))}
              </Select>
            </span>
            <Button variant="secondary" onClick={onOpenAdvanced}>
              <Braces className="h-3.5 w-3.5" aria-hidden />
              Advanced
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * "Severity" alone, sitting next to the severity *filter*, reads as a second
 * filter. Naming the action makes the control self-explanatory.
 */
function groupByLabel(option: { value: string; label: string }): string {
  return option.value === '' ? option.label : `Group by ${option.label.toLowerCase()}`;
}

function FilterField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="flex min-w-0 flex-col gap-1">
      <span className="text-[var(--text-sm)] font-medium text-[var(--text-2)]">{label}</span>
      {children}
    </label>
  );
}
